/**
 * AdminLaunchReadinessDashboard — Phase K.3 admin synthesis page.
 * Mounted at `/admin/launch-readiness`. Single API call to
 * `/api/admin/launch-readiness` populates every card.
 *
 * Cards:
 *   1. Onboarding Funnel Health
 *   2. Verification Queue Aging
 *   3. Metro / ZIP Coverage (J.1 folded in — sortable table)
 *   4. Tier Demand Funnel (embeds K.2 <AdminTierDemandFunnelCard>)
 *   4.5 Stale Intent Cohort
 *   5. Integration Status (LIVE / STUBBED / PENDING TOKEN badges)
 *   6. Test Suite Health — DEFERRED to K.3.1 (see docstring).
 *
 * Copy discipline: Family H bans apply. Admin-facing tile — no
 * copy that could leak rate-derivable framing to screencasts.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Activity,
  Clock,
  MapPin,
  TrendingUp,
  Zap,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  ShieldCheck,
  GitCommit,
  Bell,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AdminTierDemandFunnelCard } from "@/pages/admin/AdminTierDemandFunnel";
import RecruitEmailComposer, {
  RecruitSentCountBadge,
} from "@/pages/admin/RecruitEmailComposer";

/*
 * family-g-audit-whitelist: the RecruitConversionFunnelCard below
 * displays operator conversion percentages (admin-facing ops metric,
 * NOT tier/commission rates). Family G's percent bans apply only to
 * partner-facing / customer-facing surfaces; internal ops tiles are
 * explicitly whitelisted here.
 */

const _ENDPOINT = "/admin/launch-readiness";

function _formatDollars(cents) {
  const dollars = Math.round(cents) / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function _labelStatus(s) {
  return s.replace(/^(payout|verification|identity|admin|needs)_/, "")
          .replace(/_/g, " ");
}

/* ------------------------------------------------------------------ *
 * Card 1 — Onboarding Funnel
 * ------------------------------------------------------------------ */
function OnboardingFunnelCard({ funnel }) {
  return (
    <section
      data-testid="onboarding-funnel-card"
      style={_cardStyle}
    >
      <header style={_cardHeader}>
        <Activity size={18} color="#F89623" />
        <h2 style={_cardTitle}>Onboarding Funnel Health</h2>
      </header>

      {/* Highlight column: fully-live partners. */}
      <div
        data-testid="fully-live-highlight"
        style={{
          background: "#ecfdf5",
          padding: 12,
          borderRadius: 8,
          marginBottom: 12,
          border: "1px solid #a7f3d0",
        }}
      >
        <div style={{ fontSize: 12, color: "#065f46", fontWeight: 700 }}>
          Fully-live PooPartners
        </div>
        <div
          data-testid="fully-live-count"
          style={{ fontSize: 30, fontWeight: 800, color: "#065f46" }}
        >
          {funnel.fully_live_partners}
        </div>
        <div style={{ fontSize: 11, color: "#065f46" }}>
          of {funnel.total_partners} total partners in the pipeline
        </div>
      </div>

      <FunnelStageBlock
        label="Verification"
        counts={funnel.verification}
        testid="funnel-verification"
      />
      <FunnelStageBlock
        label="Payout"
        counts={funnel.payout}
        testid="funnel-payout"
      />
      <FunnelStageBlock
        label="Service area"
        counts={funnel.service_area}
        testid="funnel-service-area"
      />
      <FunnelStageBlock
        label="Pricing wizard"
        counts={funnel.pricing_wizard}
        testid="funnel-pricing-wizard"
      />
    </section>
  );
}

function FunnelStageBlock({ label, counts, testid }) {
  const entries = Object.entries(counts);
  return (
    <div style={{ marginBottom: 12 }} data-testid={testid}>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "#9ca3af",
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {entries.map(([k, v]) => (
          <span
            key={k}
            data-testid={`${testid}-${k}`}
            style={{
              background: v > 0 ? "#eff6ff" : "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              color: v > 0 ? "#1e3a8a" : "#9ca3af",
              fontWeight: 600,
            }}
          >
            <b>{v}</b> {_labelStatus(k)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Card 2 — Verification Queue Aging
 * ------------------------------------------------------------------ */
function VerificationAgingCard({ rows, totals }) {
  const empty = rows.length === 0;
  return (
    <section
      data-testid="verification-aging-card"
      style={_cardStyle}
    >
      <header style={_cardHeader}>
        <Clock size={18} color="#F89623" />
        <h2 style={_cardTitle}>Verification Queue Aging</h2>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          <span data-testid="aging-total-red" style={{ color: "#b91c1c", fontWeight: 700 }}>
            {totals.red} red
          </span>
          {"  "}·{"  "}
          <span data-testid="aging-total-yellow" style={{ color: "#a16207", fontWeight: 700 }}>
            {totals.yellow} yellow
          </span>
          {"  "}·{"  "}
          <span data-testid="aging-total-green" style={{ color: "#065f46", fontWeight: 700 }}>
            {totals.green} green
          </span>
        </span>
      </header>

      {empty && (
        <div
          data-testid="aging-empty"
          style={{ color: "#6b7280", fontSize: 13, padding: 12 }}
        >
          Queue is clear — no verifications waiting in `identity_completed`
          or `admin_in_review`.
        </div>
      )}
      {!empty && (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          data-testid="aging-table"
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb", color: "#6b7280" }}>
              <th style={{ textAlign: "left", padding: "8px 4px", fontWeight: 600 }}>Partner</th>
              <th style={{ textAlign: "left", padding: "8px 4px", fontWeight: 600 }}>Status</th>
              <th style={{ textAlign: "right", padding: "8px 4px", fontWeight: 600 }}>Age (hrs)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.partner_user_id}
                data-testid={`aging-row-${r.partner_user_id}`}
                data-bucket={r.bucket}
                style={{
                  borderBottom: "1px solid #f3f4f6",
                  background:
                    r.bucket === "red"
                      ? "#fef2f2"
                      : r.bucket === "yellow"
                      ? "#fffbeb"
                      : "transparent",
                }}
              >
                <td style={{ padding: "8px 4px", color: "#374751" }}>
                  {r.name}
                </td>
                <td style={{ padding: "8px 4px", color: "#6b7280" }}>
                  {_labelStatus(r.status)}
                </td>
                <td
                  style={{
                    padding: "8px 4px",
                    textAlign: "right",
                    fontWeight: 700,
                    color:
                      r.bucket === "red"
                        ? "#b91c1c"
                        : r.bucket === "yellow"
                        ? "#a16207"
                        : "#065f46",
                  }}
                >
                  {r.hours_in_status.toFixed(1)}h
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 10 }}>
        <Link
          to="/admin/partner-verifications"
          data-testid="aging-deeplink"
          style={{ fontSize: 12, color: "#F89623", fontWeight: 700 }}
        >
          Go to verifications queue <ChevronRight size={12} style={{ display: "inline", verticalAlign: -2 }} />
        </Link>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Card 3 — Metro Coverage (sortable, J.1 folded in)
 * ------------------------------------------------------------------ */
function MetroCoverageCard({ rows, onRecruitClick }) {
  const [sortBy, setSortBy] = useState("density");
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(() => {
    const copy = [...rows];
    if (sortBy === "density") {
      copy.sort((a, b) => b.past_30d_bookings - a.past_30d_bookings);
    } else if (sortBy === "coverage") {
      copy.sort((a, b) => b.partner_count - a.partner_count);
    } else if (sortBy === "zip") {
      copy.sort((a, b) => a.zip.localeCompare(b.zip));
    }
    return copy;
  }, [rows, sortBy]);

  const visible = showAll ? sorted : sorted.slice(0, 20);

  return (
    <section
      data-testid="metro-coverage-card"
      style={_cardStyle}
    >
      <header style={_cardHeader}>
        <MapPin size={18} color="#F89623" />
        <h2 style={_cardTitle}>Metro / ZIP Coverage</h2>
      </header>

      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <SortButton current={sortBy} value="density" setSortBy={setSortBy} label="Booking density" testid="sort-density" />
        <SortButton current={sortBy} value="coverage" setSortBy={setSortBy} label="Coverage" testid="sort-coverage" />
        <SortButton current={sortBy} value="zip" setSortBy={setSortBy} label="ZIP" testid="sort-zip" />
      </div>

      {rows.length === 0 ? (
        <div
          data-testid="metro-empty"
          style={{ color: "#6b7280", fontSize: 13, padding: 12 }}
        >
          No coverage data yet. Partners saving service areas will show up here.
        </div>
      ) : (
        <>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
            data-testid="metro-table"
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #e5e7eb", color: "#6b7280" }}>
                <th style={{ textAlign: "left", padding: "8px 4px", fontWeight: 600 }}>ZIP</th>
                <th style={{ textAlign: "right", padding: "8px 4px", fontWeight: 600 }}>Partners</th>
                <th style={{ textAlign: "right", padding: "8px 4px", fontWeight: 600 }}>30-day bookings</th>
                <th style={{ textAlign: "center", padding: "8px 4px", fontWeight: 600 }}>Signal</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.zip}
                  data-testid={`metro-row-${r.zip}`}
                  data-demand-no-supply={r.has_demand_no_supply ? "true" : "false"}
                  style={{
                    borderBottom: "1px solid #f3f4f6",
                    background: r.has_demand_no_supply ? "#fef2f2" : "transparent",
                  }}
                >
                  <td style={{ padding: "8px 4px", color: "#374751", fontFamily: "monospace" }}>
                    {r.zip}
                  </td>
                  <td style={{ padding: "8px 4px", textAlign: "right", color: "#374751" }}>
                    {r.partner_count}
                  </td>
                  <td style={{ padding: "8px 4px", textAlign: "right", color: "#374751" }}>
                    {r.past_30d_bookings}
                  </td>
                  <td style={{ padding: "8px 4px", textAlign: "center" }}>
                    {r.has_demand_no_supply ? (
                      <button
                        type="button"
                        data-testid={`metro-signal-red-${r.zip}`}
                        onClick={() =>
                          onRecruitClick &&
                          onRecruitClick({
                            zip: r.zip,
                            past_30d_bookings: r.past_30d_bookings,
                          })
                        }
                        style={{
                          background: "#fee2e2",
                          color: "#b91c1c",
                          padding: "3px 8px",
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          border: "1px solid #fca5a5",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <AlertTriangle size={11} />
                        Recruit partners
                      </button>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 11 }}>—</span>
                    )}
                    <RecruitSentCountBadge count={r.recruit_emails_sent_count} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length > 20 && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="metro-show-all-toggle"
              onClick={() => setShowAll((v) => !v)}
              style={{ marginTop: 8 }}
            >
              {showAll ? "Show top 20" : `Show all ${sorted.length}`}
            </Button>
          )}
        </>
      )}
    </section>
  );
}

function SortButton({ current, value, setSortBy, label, testid }) {
  const active = current === value;
  return (
    <button
      data-testid={testid}
      onClick={() => setSortBy(value)}
      style={{
        padding: "4px 10px",
        borderRadius: 6,
        border: "1px solid #e5e7eb",
        background: active ? "#F89623" : "#ffffff",
        color: active ? "#ffffff" : "#374751",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Card 4.5 — Stale Intent Cohort
 * ------------------------------------------------------------------ */
function StaleIntentCard({ summary }) {
  return (
    <section
      data-testid="stale-intent-card"
      style={{ ..._cardStyle, borderColor: "#fed7aa" }}
    >
      <header style={_cardHeader}>
        <AlertTriangle size={18} color="#c2410c" />
        <h2 style={_cardTitle}>Stale intents (30+ days)</h2>
      </header>
      <div
        data-testid="stale-total"
        style={{
          fontSize: 30,
          fontWeight: 800,
          color: summary.total_stale > 0 ? "#c2410c" : "#065f46",
        }}
      >
        {summary.total_stale}
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
        partners signaled Preferred/Elite intent but haven't advanced.
      </div>
      {Object.keys(summary.by_tier).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.entries(summary.by_tier).map(([tier, n]) => (
            <span
              key={tier}
              data-testid={`stale-tier-${tier}`}
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: 6,
                padding: "3px 10px",
                fontSize: 12,
                color: "#9a3412",
                fontWeight: 700,
              }}
            >
              {n} {tier}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Card 5 — Integration Status
 * ------------------------------------------------------------------ */
function IntegrationStatusCard({ rows }) {
  return (
    <section
      data-testid="integration-status-card"
      style={_cardStyle}
    >
      <header style={_cardHeader}>
        <Zap size={18} color="#F89623" />
        <h2 style={_cardTitle}>Integration Status</h2>
      </header>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {rows.map((r) => {
          const color =
            r.status === "LIVE" ? "#065f46"
              : r.status === "STUBBED" ? "#c2410c"
              : "#a16207";
          const bg =
            r.status === "LIVE" ? "#ecfdf5"
              : r.status === "STUBBED" ? "#fff7ed"
              : "#fffbeb";
          return (
            <li
              key={r.integration}
              data-testid={`integration-row-${r.integration.replace(/\s+/g, "-").toLowerCase()}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 4px",
                borderBottom: "1px solid #f3f4f6",
                gap: 8,
              }}
            >
              <span
                style={{ color: "#374751", fontSize: 13, fontWeight: 600 }}
              >
                {r.integration}
              </span>
              <span
                data-testid={`integration-badge-${r.integration.replace(/\s+/g, "-").toLowerCase()}`}
                style={{
                  background: bg,
                  color,
                  padding: "3px 10px",
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  border: `1px solid ${color}33`,
                }}
              >
                {r.status}
              </span>
            </li>
          );
        })}
      </ul>
      <div
        data-testid="integration-source-note"
        style={{
          marginTop: 8,
          fontSize: 10,
          color: "#9ca3af",
          fontStyle: "italic",
        }}
      >
        Sources: platform_config where set, static defaults otherwise.
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Card N.0 — Reminder Scheduler (Phase N.0 addition)
 *
 * Renders three states from the /admin/launch-readiness composer:
 *   - Disabled            (scheduler_enabled=False)
 *   - Not yet ticked      (enabled=True, last_tick_at=null)
 *   - Last tick + stale?  (enabled=True, last_tick_at set)
 *
 * Defensive: if the `scheduler` prop is missing entirely (e.g. an
 * older API response), renders nothing so pre-N.0 tests keep passing.
 * ------------------------------------------------------------------ */
function ReminderSchedulerCard({ scheduler }) {
  if (!scheduler) return null;
  const { enabled, last_tick_at, tick_interval_seconds } = scheduler;

  // Stale = enabled + last tick older than 1.5x the configured interval.
  // 1.5x buffer absorbs supervisor restart windows + clock jitter
  // without flapping the indicator every hour.
  let stale = false;
  let staleMinutes = null;
  if (enabled && last_tick_at) {
    const lastMs = new Date(last_tick_at).getTime();
    if (!Number.isNaN(lastMs)) {
      const ageMs = Date.now() - lastMs;
      const bufferMs = (tick_interval_seconds || 3600) * 1500;
      stale = ageMs > bufferMs;
      staleMinutes = Math.round(ageMs / 60000);
    }
  }

  let state, valueLabel;
  if (!enabled) {
    state = "disabled";
    valueLabel = "Disabled";
  } else if (!last_tick_at) {
    state = "not-yet-ticked";
    valueLabel = "Not yet ticked";
  } else {
    state = stale ? "stale" : "ticked";
    valueLabel = `Last tick: ${new Date(last_tick_at).toLocaleString("en-US")}`;
  }

  return (
    <section
      data-testid="reminder-scheduler-card"
      data-scheduler-state={state}
      style={_cardStyle}
    >
      <header style={_cardHeader}>
        <Bell size={18} color="#F89623" />
        <h2 style={_cardTitle}>Reminder Scheduler</h2>
      </header>

      <div
        data-testid="reminder-scheduler-value"
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: enabled ? "#374751" : "#9ca3af",
          marginBottom: 6,
        }}
      >
        {valueLabel}
      </div>

      {stale && (
        <div
          data-testid="reminder-scheduler-stale-warning"
          style={{
            padding: "8px 12px",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
            color: "#a16207",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <AlertTriangle size={14} />
          <span>
            Last tick was ~{staleMinutes} min ago, expected every{" "}
            {Math.round((tick_interval_seconds || 3600) / 60)} min. Check
            supervisor logs.
          </span>
        </div>
      )}

      {enabled && (
        <div
          data-testid="reminder-scheduler-interval"
          style={{
            fontSize: 11,
            color: "#9ca3af",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            marginTop: 8,
          }}
        >
          Tick interval: {Math.round((tick_interval_seconds || 3600) / 60)} min
        </div>
      )}
    </section>
  );
}


/* ------------------------------------------------------------------ *
 * Common card styles
 * ------------------------------------------------------------------ */
const _cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
  background: "#ffffff",
};

const _cardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 12,
};

const _cardTitle = {
  fontSize: 16,
  fontWeight: 800,
  margin: 0,
  color: "#374751",
};

/* ------------------------------------------------------------------ *
 * Card 6 — Test Suite Health (K.3.1 addition)
 * ------------------------------------------------------------------ */
function TestSuiteHealthCard({ health }) {
  if (!health) return null;
  const { pytest, yarn, stale_snapshot_hours } = health;
  const stale = stale_snapshot_hours != null && stale_snapshot_hours >= 24;
  return (
    <section data-testid="test-suite-health-card" style={_cardStyle}>
      <header style={_cardHeader}>
        <ShieldCheck size={18} color="#F89623" />
        <h2 style={_cardTitle}>Test Suite Health</h2>
      </header>

      {stale && (
        <div
          data-testid="test-suite-health-stale-banner"
          style={{
            padding: "8px 12px",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
            color: "#a16207",
            fontSize: 12,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <AlertTriangle size={14} />
          <span>
            Snapshot stale — last run{" "}
            {stale_snapshot_hours < 48
              ? `${Math.round(stale_snapshot_hours)}h`
              : `${Math.round(stale_snapshot_hours / 24)}d`}{" "}
            ago. Run pytest + yarn to refresh.
          </span>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        }}
      >
        <SuitePanel label="Backend (pytest)" snap={pytest} testid="pytest-panel" />
        <SuitePanel label="Frontend (yarn)" snap={yarn} testid="yarn-panel" />
      </div>

      {pytest && pytest.sentinels && Object.keys(pytest.sentinels).length > 0 && (() => {
        const sentinelEntries = Object.entries(pytest.sentinels);
        const failingCount = sentinelEntries.filter(([, v]) => !v).length;
        const allGreen = failingCount === 0;
        return (
        <div style={{ marginTop: 14 }}>
          <div
            data-testid="sentinel-summary-label"
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              color: allGreen ? "#9ca3af" : "#b91c1c",
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            {sentinelEntries.length} migration sentinels
            {allGreen ? " — all green" : ` — ${failingCount} failing`}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {sentinelEntries.map(([k, v]) => (
              <span
                key={k}
                data-testid={`sentinel-chip-${k}`}
                style={{
                  background: v ? "#ecfdf5" : "#fef2f2",
                  color: v ? "#065f46" : "#b91c1c",
                  padding: "3px 8px",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  border: `1px solid ${v ? "#a7f3d0" : "#fecaca"}`,
                }}
              >
                {v ? "OK" : "FAIL"} {k.replace(/^platform_config_/, "").replace(/_migrated$/, "")}
              </span>
            ))}
          </div>
        </div>
        );
      })()}
    </section>
  );
}

function SuitePanel({ label, snap, testid }) {
  if (!snap) {
    return (
      <div
        data-testid={`${testid}-missing`}
        style={{
          padding: 12,
          borderRadius: 8,
          border: "1px dashed #e5e7eb",
          color: "#9ca3af",
          fontSize: 12,
          textAlign: "center",
        }}
      >
        <div style={{ fontWeight: 700 }}>{label}</div>
        <div style={{ marginTop: 4 }}>No snapshot yet</div>
      </div>
    );
  }
  const failing = (snap.fail_count || 0) > 0;
  return (
    <div
      data-testid={testid}
      style={{
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${failing ? "#fca5a5" : "#a7f3d0"}`,
        background: failing ? "#fef2f2" : "#ecfdf5",
      }}
    >
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: failing ? "#b91c1c" : "#065f46",
        }}
        data-testid={`${testid}-pass-count`}
      >
        {snap.pass_count}
      </div>
      <div style={{ fontSize: 11, color: "#374751" }}>passing</div>
      {failing && (
        <div
          data-testid={`${testid}-fail-count`}
          style={{ fontSize: 12, color: "#b91c1c", fontWeight: 700, marginTop: 4 }}
        >
          {snap.fail_count} failing
        </div>
      )}
      {snap.skip_count > 0 && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
          {snap.skip_count} skipped
        </div>
      )}
      <div
        style={{
          marginTop: 8,
          fontSize: 10,
          color: "#6b7280",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <GitCommit size={10} />
        <span data-testid={`${testid}-sha`}>{snap.last_run_git_sha}</span>
        {snap.duration_seconds != null && (
          <>
            <span>·</span>
            <span>{snap.duration_seconds.toFixed(1)}s</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Card 7 — Recruit Conversion Funnel (K.3.1 addition)
 * family-g-audit-whitelist: this card renders admin ops percentages.
 * ------------------------------------------------------------------ */
function RecruitConversionFunnelCard({ funnel }) {
  if (!funnel) return null;
  const empty = (funnel.cohorts || []).length === 0;
  return (
    <section
      data-testid="recruit-conversion-funnel-card"
      style={_cardStyle}
    >
      <header style={_cardHeader}>
        <TrendingUp size={18} color="#F89623" />
        <h2 style={_cardTitle}>Recruit Conversion Funnel</h2>
      </header>

      {empty ? (
        <div
          data-testid="conversion-funnel-empty"
          style={{ color: "#6b7280", fontSize: 13, padding: 12 }}
        >
          No recruit emails sent yet — send some from the Metro Coverage
          card above.
        </div>
      ) : (
        <>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
            data-testid="conversion-funnel-table"
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #e5e7eb", color: "#6b7280" }}>
                <th style={_thStyle("left")}>Cohort</th>
                <th style={_thStyle("right")}>Sends</th>
                <th style={_thStyle("right")}>Converted</th>
                <th style={_thStyle("right")}>Pending</th>
                <th style={_thStyle("right")}>Expired</th>
                <th style={_thStyle("right")}>Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr
                data-testid="conversion-funnel-totals-row"
                style={{
                  background: "#f9fafb",
                  fontWeight: 700,
                  borderBottom: "2px solid #d1d5db",
                }}
              >
                <td style={_tdStyle("left")}>Totals</td>
                <td style={_tdStyle("right")}>{funnel.totals.sends_count}</td>
                <td
                  style={{ ..._tdStyle("right"), color: "#065f46" }}
                  data-testid="conversion-funnel-totals-converted"
                >
                  {funnel.totals.converted_count}
                </td>
                <td style={_tdStyle("right")}>{funnel.totals.pending_count}</td>
                <td style={_tdStyle("right")}>{funnel.totals.expired_count}</td>
                <td
                  style={{ ..._tdStyle("right"), color: "#065f46" }}
                  data-testid="conversion-funnel-totals-rate"
                >
                  {funnel.totals.conversion_rate_pct == null
                    ? "-"
                    : `${funnel.totals.conversion_rate_pct}%`}
                </td>
              </tr>
              {funnel.cohorts.map((c) => (
                <tr
                  key={c.cohort_month}
                  data-testid={`conversion-cohort-${c.cohort_month}`}
                  style={{ borderBottom: "1px solid #f3f4f6" }}
                >
                  <td style={_tdStyle("left")}>{c.cohort_month}</td>
                  <td style={_tdStyle("right")}>{c.sends_count}</td>
                  <td style={{ ..._tdStyle("right"), color: "#065f46" }}>
                    {c.converted_count}
                  </td>
                  <td style={_tdStyle("right")}>{c.pending_count}</td>
                  <td style={_tdStyle("right")}>{c.expired_count}</td>
                  <td
                    style={_tdStyle("right")}
                    data-testid={`conversion-cohort-rate-${c.cohort_month}`}
                  >
                    {c.conversion_rate_pct == null
                      ? "-"
                      : `${c.conversion_rate_pct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              marginTop: 8,
              fontSize: 10,
              color: "#9ca3af",
              fontStyle: "italic",
            }}
          >
            Rate excludes pending (still inside the 90-day attribution
            window) from the denominator.
          </div>
        </>
      )}
    </section>
  );
}

const _thStyle = (align) => ({
  textAlign: align,
  padding: "6px 4px",
  fontWeight: 600,
});
const _tdStyle = (align) => ({
  textAlign: align,
  padding: "6px 4px",
  color: "#374751",
});


/* ------------------------------------------------------------------ *
 * Main page component
 * ------------------------------------------------------------------ */
export default function AdminLaunchReadinessDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [recruitTarget, setRecruitTarget] = useState(null);   // {zip, past_30d_bookings} | null

  const load = useCallback(async () => {
    setLoading(true);
    setErrored(false);
    try {
      const res = await api.get(_ENDPOINT);
      setData(res.data);
    } catch (e) {
      setErrored(true);
      // eslint-disable-next-line no-console
      console.warn(
        "launch-readiness fetch failed:",
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, retryKey]);

  return (
    <div
      data-testid="launch-readiness-page"
      style={{ padding: "24px 20px", maxWidth: 1200 }}
    >
      <header style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              color: "#F89623",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            Launch Readiness
          </span>
        </div>
        <h1
          data-testid="launch-readiness-heading"
          style={{
            fontSize: 28,
            fontWeight: 800,
            margin: "6px 0 0 0",
            color: "#374751",
          }}
        >
          Are we ready to launch?
        </h1>
        {data?.as_of && (
          <div
            data-testid="launch-readiness-asof"
            style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}
          >
            snapshot generated {new Date(data.as_of).toLocaleString("en-US")}
          </div>
        )}
      </header>

      {loading && (
        <div
          data-testid="launch-readiness-loading"
          style={{ padding: 60, textAlign: "center" }}
        >
          <Loader2 className="animate-spin" size={24} />
        </div>
      )}

      {!loading && errored && (
        <div
          data-testid="launch-readiness-error"
          style={{
            padding: 24,
            border: "1px solid #fecaca",
            borderRadius: 12,
            background: "#fef2f2",
            textAlign: "center",
          }}
        >
          <div
            style={{ color: "#b91c1c", fontSize: 14, marginBottom: 10 }}
          >
            Something interrupted the snapshot. Try again in a moment.
          </div>
          <Button
            data-testid="launch-readiness-retry"
            onClick={() => setRetryKey((k) => k + 1)}
            size="sm"
          >
            <RefreshCw size={14} style={{ marginRight: 6 }} />
            Retry
          </Button>
        </div>
      )}

      {!loading && !errored && data && (
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          }}
        >
          <OnboardingFunnelCard funnel={data.onboarding_funnel} />
          <VerificationAgingCard
            rows={data.verification_aging}
            totals={data.verification_aging_totals}
          />
          <MetroCoverageCard
            rows={data.metro_coverage}
            onRecruitClick={(payload) => setRecruitTarget(payload)}
          />
          <div>
            <AdminTierDemandFunnelCard
              data={data.tier_demand_funnel}
              loading={false}
              error={false}
            />
            <div style={{ marginTop: 16 }}>
              <StaleIntentCard summary={data.stale_intent_summary} />
            </div>
          </div>
          <IntegrationStatusCard rows={data.integration_status} />
          <TestSuiteHealthCard health={data.test_suite_health} />
          <RecruitConversionFunnelCard funnel={data.recruit_conversion_funnel} />
          <ReminderSchedulerCard scheduler={data.reminder_scheduler} />
        </div>
      )}

      <RecruitEmailComposer
        open={recruitTarget !== null}
        onClose={() => setRecruitTarget(null)}
        zipCode={recruitTarget?.zip || ""}
        pastBookings={recruitTarget?.past_30d_bookings || 0}
        onSent={() => {
          // Refresh dashboard so the "N sent" badge updates.
          setRetryKey((k) => k + 1);
        }}
      />
    </div>
  );
}
